import React, { useState, useEffect, useMemo } from "react";
import { useGetAdminPageDataQuery } from "state/api";
import { useTheme } from "@mui/material/styles";
import { DownloadOutlined } from "@mui/icons-material";
import { RingLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import * as XLSX from "xlsx";
import { useDispatch } from "react-redux";
import { setRmMgName } from "state/authSlice";
import { List } from "@mui/icons-material";

const Admin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: rawResponse, isLoading, error } = useGetAdminPageDataQuery({});
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();

  useEffect(() => {
    if (rawResponse && Array.isArray(rawResponse) && rawResponse[0]) {
      setFilteredData(rawResponse[0]);
    }
  }, [rawResponse]);

  // Extract unique dates and products from data
  const { dateColumns, products } = useMemo(() => {
    if (filteredData.length === 0) return { dateColumns: [], products: [] };

    const uniqueDates = new Set();
    const uniqueProducts = new Set();

    filteredData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== "RmMgName") {
          const parts = key.split("_");
          if (parts.length >= 4) {
            // Extract date in DD_MM_YYYY format
            const datePart = parts.slice(0, 3).join("_");
            uniqueDates.add(datePart);

            // Extract product (3rd part is year, 4th is product)
            if (parts.length >= 4) {
              uniqueProducts.add(parts[3]);
            }
          }
        }
      });
    });

    // Sort dates chronologically
    const sortedDates = Array.from(uniqueDates).sort((a, b) => {
      const [dayA, monthA, yearA] = a.split("_");
      const [dayB, monthB, yearB] = b.split("_");
      const dateA = new Date(`${yearA}-${monthA}-${dayA}`);
      const dateB = new Date(`${yearB}-${monthB}-${dayB}`);
      return dateA - dateB;
    });

    return {
      dateColumns: sortedDates,
      products: Array.from(uniqueProducts),
    };
  }, [filteredData]);

  // Map raw region names to display names
  const mapRegionName = (name) => {
    const mappings = {};

    return mappings[name] || name;
  };

  // Format date to DD MMM
  const formatDateHeader = (dateStr) => {
    const [day, month, year] = dateStr.split("_");
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
  };

  const downloadExcel = () => {
    // Map region names for Excel export
    const dataForExport = filteredData.map((row) => ({
      ...row,
      TeamName: mapRegionName(row.RmMgName),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RM Daily Performance");
    XLSX.writeFile(workbook, "rm_daily_performance.xlsx");
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (rawResponse && rawResponse[0]) {
      const filtered = rawResponse[0].filter((region) =>
        mapRegionName(region.RmMgName)
          .toLowerCase()
          .includes(term.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  };

  const handleTeamClick = (teamName) => {
    dispatch(setRmMgName(teamName));
    navigate("/rm-details");
  };

  const CustomSpinner = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: theme.palette.background.default,
      }}
    >
      <RingLoader color={theme.palette.primary.main} size={100} />
    </Box>
  );

  if (isLoading) return <CustomSpinner />;
  if (error)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
          textAlign: "center",
          padding: 3,
        }}
      >
        <Typography variant="h5" color="error">
          Error loading data
        </Typography>
        <Typography variant="body1">{error.message}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </Box>
    );

  return (
    <Box
      sx={{
        padding: { xs: 2, md: 4 },
        maxWidth: "1800px",
        margin: "0 auto",
        minHeight: "100vh",
        background: theme.palette.background.default,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 3,
          mb: 4,
          padding: 3,
          borderRadius: 4,
          background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: 3,
        }}
      >
        <TextField
          label="Search RmMgName"
          variant="filled"
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            width: "100%",
            "& .MuiFilledInput-root": {
              background: theme.palette.background.paper,
              borderRadius: 2,
              "&:before": { borderBottom: "none" },
              "&:hover": { background: theme.palette.background.paper },
            },
          }}
          InputProps={{
            disableUnderline: true,
          }}
        />

        <Box
          sx={{
            display: "flex",
            gap: 2,
            width: { xs: "100%", sm: "auto" },
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            onClick={() => navigate("/Dailywise")}
            variant="contained"
            startIcon={<List />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: "bold",
              textTransform: "none",
              background: theme.palette.success.main,
              color: theme.palette.getContrastText(theme.palette.success.main),
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: 4,
                background: theme.palette.success.dark,
              },
              transition: "all 0.3s ease",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Prescription Score
          </Button>
          <Button
            onClick={() => navigate("/DailyWisePob")}
            variant="contained"
            // startIcon={<Add />}
            startIcon={<List />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: "bold",
              textTransform: "none",
              background: theme.palette.success.main,
              color: theme.palette.getContrastText(theme.palette.success.main),
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: 4,
                background: theme.palette.success.dark,
              },
              transition: "all 0.3s ease",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Pob Score
          </Button>
          <Button
            onClick={() => navigate("/imagePreviewModal")}
            variant="contained"
            startIcon={<List />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: "bold",
              textTransform: "none",
              background: theme.palette.success.main,
              color: theme.palette.getContrastText(theme.palette.success.main),
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: 4,
                background: theme.palette.success.dark,
              },
              transition: "all 0.3s ease",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Preview Images
          </Button>

          <Button
            onClick={() => navigate("/imagePreviewModal")}
            variant="contained"
            startIcon={<List />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: "bold",
              textTransform: "none",
              background: theme.palette.info.main,
              color: theme.palette.getContrastText(theme.palette.info.main),
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: 4,
                background: theme.palette.info.dark,
              },
              transition: "all 0.3s ease",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Pob Images
          </Button>

          <Button
            onClick={downloadExcel}
            variant="contained"
            startIcon={<DownloadOutlined />}
            sx={{
              py: 1,
              px: 2.5,
              borderRadius: 2,
              fontWeight: "bold",
              textTransform: "none",
              background: theme.palette.secondary.main,
              color: theme.palette.getContrastText(
                theme.palette.secondary.main,
              ),
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: 4,
                background: theme.palette.secondary.dark,
              },
              transition: "all 0.3s ease",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* Data Table */}
      {filteredData.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 4,
            boxShadow: 3,
            maxHeight: "70vh",
            overflow: "auto",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table stickyHeader aria-label="daily approval table">
            <TableHead>
              {/* Date Header Row */}
              <TableRow>
                <TableCell
                  rowSpan={3}
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    position: "sticky",
                    left: 0,
                    zIndex: 20,
                    minWidth: 200,
                    verticalAlign: "middle",
                  }}
                >
                  RmMgName
                </TableCell>
                {dateColumns.map((date) => (
                  <TableCell
                    key={`date-${date}`}
                    align="center"
                    colSpan={products.length * 3} // 3 statuses per product
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.common.white,
                    }}
                  >
                    {formatDateHeader(date)}
                  </TableCell>
                ))}
              </TableRow>

              {/* Product Header Row */}
              <TableRow>
                {dateColumns.flatMap((date) =>
                  products.map((product) => (
                    <TableCell
                      key={`${date}-${product}`}
                      align="center"
                      colSpan={3}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: theme.palette.primary.light,
                      }}
                    >
                      {product}
                    </TableCell>
                  )),
                )}
              </TableRow>

              {/* Status Header Row */}
              <TableRow>
                {dateColumns.flatMap((date) =>
                  products.flatMap((product) =>
                    ["Approved", "Rejected", "Pending"].map((status) => (
                      <TableCell
                        key={`${date}-${product}-${status}`}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          backgroundColor:
                            status === "Approved"
                              ? theme.palette.success.light
                              : status === "Rejected"
                                ? theme.palette.error.light
                                : theme.palette.warning.light,
                        }}
                      >
                        {status}
                      </TableCell>
                    )),
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover>
                  <TableCell
                    onClick={() => handleTeamClick(row.RmMgName)}
                    sx={{
                      fontWeight: "bold",
                      position: "sticky",
                      left: 0,
                      backgroundColor: theme.palette.background.paper,
                      zIndex: 10,
                      minWidth: 200,
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                        color: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    {mapRegionName(row.RmMgName)}
                  </TableCell>

                  {dateColumns.flatMap((date) =>
                    products.flatMap((product) =>
                      ["Approved", "Rejected", "Pending"].map((status) => {
                        const key = `${date}_${product}_${status}`;
                        return (
                          <TableCell
                            key={key}
                            align="center"
                            sx={{
                              backgroundColor:
                                status === "Approved"
                                  ? theme.palette.success.light
                                  : status === "Rejected"
                                    ? theme.palette.error.light
                                    : theme.palette.warning.light,
                            }}
                          >
                            {row[key] || "0"}
                          </TableCell>
                        );
                      }),
                    ),
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            textAlign: "center",
            padding: 4,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 4,
            background: theme.palette.background.paper,
          }}
        >
          <Typography variant="h6" color="textSecondary">
            {filteredData.length === 0 && !isLoading
              ? "No data available"
              : "Loading data..."}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Admin;
